/**
 * Query string builder matching what axios sent to the API (and what Laravel
 * parses): `ids[]=1&ids[]=2` for a top-level list, indexed brackets below
 * (`filters[type][0]=a`, `rows[0][id]=3`), `null`/`undefined` dropped,
 * dates as ISO strings, spaces as `+`.
 */

type QueryValue = unknown

const encode = (text: string): string =>
  encodeURIComponent(text)
    .replace(/%3A/gi, ':')
    .replace(/%24/g, '$')
    .replace(/%2C/gi, ',')
    .replace(/%20/g, '+')

const isMissing = (value: QueryValue): boolean =>
  value === null || value === undefined

const isNested = (value: QueryValue): boolean =>
  typeof value === 'object' && value !== null && !(value instanceof Date)

function scalar(value: QueryValue): string {
  return value instanceof Date ? value.toISOString() : String(value)
}

function collect(
  key: string,
  value: QueryValue,
  pairs: string[],
  isTopLevel: boolean
): void {
  if (isMissing(value)) {
    return
  }

  if (Array.isArray(value)) {
    const isFlatList = isTopLevel && !value.some(isNested)

    value.forEach((item, position) =>
      collect(
        isFlatList ? `${key}[]` : `${key}[${position}]`,
        item,
        pairs,
        false
      )
    )
    return
  }

  if (isNested(value)) {
    for (const [childKey, childValue] of Object.entries(value as object)) {
      collect(`${key}[${childKey}]`, childValue, pairs, false)
    }
    return
  }

  pairs.push(`${encode(key)}=${encode(scalar(value))}`)
}

export function serializeParams(
  params: Record<string, QueryValue> | URLSearchParams | undefined | null
): string {
  if (!params) {
    return ''
  }

  if (params instanceof URLSearchParams) {
    return params.toString()
  }

  const pairs: string[] = []

  for (const [key, value] of Object.entries(params)) {
    collect(key, value, pairs, true)
  }

  return pairs.join('&')
}
